import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { installConsoleCapture } from "./platform/consoleBuffer";
import { preloadKioskLanguage } from "./i18n/store";
import { installStaleBuildRecovery } from "./platform/staleBuild";
import { initTheme, ThemeProvider } from "./theme/ThemeProvider";
import "./styles/base.css";

/**
 * G-72: start capturing BEFORE React mounts.
 *
 * The errors worth having are the ones thrown while the app is still starting
 * — a failed boot, a bad token, a module that will not load. Installing this
 * inside a component would miss exactly those.
 */
installConsoleCapture();

// G-101: before anything lazy is asked for. A tablet whose WebView has been
// open across a deploy cannot fetch ANY chunk the new build renamed — the
// dictionaries, the scanner — and one reload onto the current build is the
// only repair. Guarded to once per page load, so a tablet that is merely
// offline cannot be put into a reload loop.
installStaleBuildRecovery();

// Stamp data-theme before the first paint so there is no light/dark flash.
initTheme();

// G-85: start fetching the kiosk dictionary now, for the same reason — the
// lock screen should not flash English before its own language lands. The
// store decides whether to: a browser that has ever had a language chosen on
// it is a kiosk tablet and always preloads, which is what covers reaching the
// kiosk by in-app navigation; anything else only preloads when the page was
// opened ON the kiosk, so a CRM session fetches nothing it will not render.
// A string compare, deliberately, rather than importing the router before
// React has mounted.
preloadKioskLanguage(window.location.pathname.startsWith("/app/in-the-shop"));

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Missing root element");
}

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </StrictMode>,
);
